import React from 'react';

interface VestingChartProps {
  totalMonths: number;
  cliffMonths: number;
  // Months since the chart origin to today's marker.
  currentMonth: number;
  vestedPercent: number;
  offboardMonth?: number;
  // Months to shift the unlock curve right of the vesting curve, so its cliff
  // lands on the track's fixed unlock date rather than on cliffMonths. The caller
  // derives it from that date; 0 means the date is not set yet.
  unlockOffsetMonths: number;
}

interface Pt {
  m: number;
  v: number;
}

const buildCurve = (cliffMonths: number, totalMonths: number): Pt[] => {
  const pts: Pt[] = [];
  for (let m = 0; m <= totalMonths; m++) {
    let v: number;
    if (m < cliffMonths) v = 0;
    else if (m === cliffMonths) v = totalMonths === cliffMonths ? 100 : 25;
    else v = 25 + (75 * (m - cliffMonths)) / Math.max(1, totalMonths - cliffMonths);
    pts.push({ m, v });
  }
  return pts;
};

const pickTicks = (totalMonths: number): number[] => {
  if (totalMonths <= 0) return [0];
  const desired = 7;
  const step = Math.max(1, Math.round(totalMonths / (desired - 1)));
  const ticks: number[] = [];
  for (let i = 0; i <= totalMonths; i += step) ticks.push(i);
  if (ticks[ticks.length - 1] !== totalMonths) ticks.push(totalMonths);
  return ticks;
};

export const VestingChart: React.FC<VestingChartProps> = ({
  totalMonths,
  cliffMonths,
  currentMonth,
  vestedPercent,
  offboardMonth,
  unlockOffsetMonths,
}) => {
  const W = 880;
  const H = 240;
  const PL = 40;
  const PR = 24;
  const PT = 32;
  const PB = 36;
  const safeTotal = Math.max(1, totalMonths);
  const safeCliff = Math.min(Math.max(0, cliffMonths), safeTotal);
  // The unlock cliff is a fixed calendar date while the vesting cliff is anchored
  // on the onboard date, so either can come first: a recent joiner's unlock date
  // can precede their vesting cliff, making unlockOffsetMonths NEGATIVE. Pad
  // whichever curve starts later instead of clamping the offset at 0, which would
  // draw the unlock curve up to a year after the date printed beside the chart.
  const vestPad = Math.max(0, -unlockOffsetMonths);
  const unlockPad = Math.max(0, unlockOffsetMonths);
  // X-axis spans both schedules end to end so neither curve is clipped.
  const chartTotal = safeTotal + Math.abs(unlockOffsetMonths);
  const pts = buildCurve(safeCliff, safeTotal);

  const x = (m: number) => PL + (m / chartTotal) * (W - PL - PR);
  // Month 0 of the vesting curve is the onboard date; month 0 of the unlock curve
  // is its own cliff minus CLIFF_DURATION. xv/xu place each in that shared axis.
  const xv = (m: number) => x(m + vestPad);
  const xu = (m: number) => x(m + unlockPad);
  const y = (v: number) => H - PB - (v / 100) * (H - PT - PB);

  const isOffboarded = typeof offboardMonth === 'number' && offboardMonth >= 0;
  const clampedOffboard =
    isOffboarded ? Math.min(Math.max(0, offboardMonth!), safeTotal) : safeTotal;

  // Split the curve: solid up to off-board, then plateau (dashed) afterward.
  const activePts = isOffboarded ? pts.filter((p) => p.m <= clampedOffboard) : pts;
  const linePath = activePts.map((p, i) => `${i ? 'L' : 'M'}${xv(p.m)},${y(p.v)}`).join(' ');

  // After off-board, draw a plateau at the frozen value (dashed) instead of the
  // original ascending schedule — communicates the cancelled vesting. Extends
  // to chartTotal so the freeze remains visible across the full timeline.
  const frozenValue = isOffboarded
    ? (activePts.length ? activePts[activePts.length - 1].v : 0)
    : 0;
  const plateauPath = isOffboarded
    ? `M${xv(clampedOffboard)},${y(frozenValue)} L${x(chartTotal)},${y(frozenValue)}`
    : '';

  const clampedCurrent = Math.min(Math.max(0, currentMonth), clampedOffboard);
  const fillPts = pts.filter((p) => p.m <= clampedCurrent);
  const fillPath = fillPts.length
    ? [
        `M${xv(0)},${y(0)}`,
        ...fillPts.map((p) => `L${xv(p.m)},${y(p.v)}`),
        `L${xv(clampedCurrent)},${y(0)}`,
        'Z',
      ].join(' ')
    : '';

  // Unlock curve: same shape as vesting, placed via xu so its cliff sits exactly
  // on the track's fixed date whichever side of the vesting cliff that falls.
  const unlockPts = buildCurve(safeCliff, safeTotal);
  const unlockPath = unlockPts
    .map((p, i) => `${i ? 'L' : 'M'}${xu(p.m)},${y(p.v)}`)
    .join(' ');

  // Ticks stay labelled against the vesting origin (M0 = onboard), so drop any
  // that fall in the pre-onboard padding rather than printing negative months.
  const ticks = pickTicks(chartTotal).filter((m) => m >= vestPad);
  const vAxis = [0, 25, 50, 75, 100];

  // Clamped AND checked for finiteness. The caller derives this from bigint
  // amounts, and a grant resized below what its holder has already claimed makes
  // those ratios exceed one or, with a zero ceiling, divide by zero — either of
  // which would put the marker off the top of the plot or emit `M NaN,NaN` and
  // blank the whole SVG. Neither is worth risking for one number.
  const todayY = Number.isFinite(vestedPercent) ? Math.min(100, Math.max(0, vestedPercent)) : 0;

  const todayLabel = `TODAY · ${todayY.toFixed(1)}% VESTED`;
  // Mono glyphs at fontSize 11 with 0.05em tracking run ~7.2 units wide. Late in the
  // schedule the marker sits near the right edge, where a left-anchored label would
  // run past the viewBox and clip; flip it to the other side of the marker instead.
  const todayLabelFlipped = xv(clampedCurrent) + 10 + todayLabel.length * 7.2 > W;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" style={{ display: 'block' }}>
      <line
        x1={PL}
        x2={W - PR}
        y1={y(0)}
        y2={y(0)}
        stroke="var(--border-2)"
        strokeWidth="1"
      />

      {vAxis.map((v) =>
        v === 0 ? null : (
          <g key={v}>
            <line
              x1={PL}
              x2={W - PR}
              y1={y(v)}
              y2={y(v)}
              stroke="var(--border)"
              strokeDasharray="2 5"
            />
            <text
              x={PL - 8}
              y={y(v) + 3}
              fill="var(--text-muted)"
              fontSize="10"
              fontFamily="var(--font-mono)"
              textAnchor="end"
              letterSpacing="0.05em"
            >
              {v}%
            </text>
          </g>
        ),
      )}

      {safeCliff > 0 && safeCliff < safeTotal && (
        <>
          <line
            x1={xv(safeCliff)}
            x2={xv(safeCliff)}
            y1={PT}
            y2={H - PB}
            stroke="var(--border-2)"
            strokeDasharray="3 4"
          />
          <text
            x={xv(safeCliff) + 6}
            y={PT + 10}
            fill="var(--text-muted)"
            fontSize="10"
            fontFamily="var(--font-mono)"
            letterSpacing="0.14em"
          >
            VEST CLIFF
          </text>
        </>
      )}

      {/* The unlock cliff is a fixed calendar date, normally well past the vesting
          one, so it gets its own marker -- in the unlock curve's color, and on a
          second line so the two labels don't collide when the offset is small.
          Skipped only at offset 0 (date unset, or exactly on the vesting cliff),
          where it would just double the marker above. */}
      {unlockOffsetMonths !== 0 && safeCliff > 0 && safeCliff < safeTotal && (
        <>
          <line
            x1={xu(safeCliff)}
            x2={xu(safeCliff)}
            y1={PT}
            y2={H - PB}
            stroke="var(--info)"
            strokeDasharray="3 4"
            opacity="0.5"
          />
          <text
            x={xu(safeCliff) + 6}
            y={PT + 24}
            fill="var(--info)"
            fontSize="10"
            fontFamily="var(--font-mono)"
            letterSpacing="0.14em"
          >
            UNLOCK CLIFF
          </text>
        </>
      )}

      {ticks.map((m) => (
        <text
          key={m}
          x={x(m)}
          y={H - 12}
          fill="var(--text-muted)"
          fontSize="10"
          fontFamily="var(--font-mono)"
          textAnchor="middle"
          letterSpacing="0.05em"
        >
          M{Math.round(m - vestPad)}
        </text>
      ))}

      {fillPath && <path d={fillPath} fill="var(--accent-soft)" />}
      <path d={linePath} stroke="var(--accent)" strokeWidth="1.5" fill="none" />
      {unlockPath && (
        <path
          d={unlockPath}
          stroke="var(--info)"
          strokeWidth="1.5"
          strokeDasharray="6 4"
          fill="none"
        />
      )}
      {plateauPath && (
        <path
          d={plateauPath}
          stroke="var(--text-muted)"
          strokeWidth="1"
          strokeDasharray="4 4"
          fill="none"
        />
      )}
      {isOffboarded && clampedOffboard < safeTotal && (
        <>
          <line
            x1={xv(clampedOffboard)}
            x2={xv(clampedOffboard)}
            y1={PT}
            y2={H - PB}
            stroke="var(--danger)"
            strokeWidth="1"
            strokeDasharray="3 4"
            opacity="0.7"
          />
          <text
            x={xv(clampedOffboard) + 6}
            y={PT + 10}
            fill="var(--danger)"
            fontSize="10"
            fontFamily="var(--font-mono)"
            letterSpacing="0.14em"
          >
            OFF-BOARD
          </text>
        </>
      )}

      <line
        x1={xv(clampedCurrent)}
        x2={xv(clampedCurrent)}
        y1={PT}
        y2={H - PB}
        stroke="var(--accent)"
        strokeWidth="1"
        opacity="0.5"
      />
      <circle
        cx={xv(clampedCurrent)}
        cy={y(todayY)}
        r="5"
        fill="var(--accent)"
        stroke="var(--bg)"
        strokeWidth="2"
      />
      <text
        x={todayLabelFlipped ? xv(clampedCurrent) - 10 : xv(clampedCurrent) + 10}
        y={y(todayY) - 10}
        textAnchor={todayLabelFlipped ? 'end' : 'start'}
        fill="var(--text)"
        fontSize="11"
        fontFamily="var(--font-mono)"
        letterSpacing="0.05em"
      >
        {todayLabel}
      </text>
    </svg>
  );
};
